import { TreeItemProps } from '@material-ui/lab/TreeItem/TreeItem';
import React, { useEffect, useState } from 'react';
import folderIcon from '../../assets/img/folder.svg';
import { createStyles, makeStyles, Theme } from '@material-ui/core';
import {
  TreeItem as MuiTreeItem,
  TreeView as MuiTreeView,
  TreeViewPropsBase,
} from '@material-ui/lab';
import { SingleSelectTreeViewProps } from '@material-ui/lab/TreeView/TreeView';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';

const useOnNodeToggle = (initialExpandedNodes: string[] | undefined) => {
  const [expandedNodes, setState] = useState<string[]>([]);
  const onNodeToggle: TreeViewPropsBase['onNodeToggle'] = (event, newExpandedNodeIds) => {
    setState(newExpandedNodeIds);
  };

  useEffect(() => {
    setState(initialExpandedNodes || []);
  }, [initialExpandedNodes]);

  return { expandedNodes, onNodeToggle };
};

export const TreeView: React.FC<SingleSelectTreeViewProps> = ({
  children,
  expanded,
  selected,
  onNodeSelect,
}) => {
  const classes = useFolderTreeStyles();
  const { expandedNodes, onNodeToggle } = useOnNodeToggle(expanded);

  return (
    <MuiTreeView
      className={classes.treeRoot}
      defaultCollapseIcon={<ArrowDropDownIcon className={classes.arrowIcon} />}
      defaultExpandIcon={<ArrowRightIcon className={classes.arrowIcon} />}
      onNodeSelect={onNodeSelect}
      expanded={expandedNodes}
      selected={selected}
      onNodeToggle={onNodeToggle}
    >
      {children}
    </MuiTreeView>
  );
};

export const TreeItem: React.FunctionComponent<
  TreeItemProps & { preventNodeToggleOnLabelClick?: boolean }
> = ({ preventNodeToggleOnLabelClick, onLabelClick, ...props }) => {
  const wrappedOnLabelClick: TreeItemProps['onLabelClick'] = (e) => {
    if (preventNodeToggleOnLabelClick) {
      e.preventDefault();
    }
    onLabelClick && onLabelClick(e);
  };

  return <MuiTreeItem {...props} data-testid={props.nodeId} onLabelClick={wrappedOnLabelClick} />;
};

export const TreeItemLabel: React.FC<{ name?: string; src?: string }> = ({
  name,
  src = folderIcon,
}) => {
  const classes = useFolderTreeStyles();
  return (
    <>
      <img alt={`${name} icon`} className={classes.treeItemIcon} src={src} />
      <span>{name}</span>
    </>
  );
};

export const useFolderTreeStyles = makeStyles((theme: Theme) =>
  createStyles({
    treeRoot: {
      maxHeight: 360,
      overflowY: 'scroll',
      border: `1px solid ${theme.palette.grey[400]}`,
    },
    treeItemBase: {
      fontSize: '16px',
      padding: '6px 0',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
    },
    treeItemRoot: {
      "&:focus div[class*='treeItemCurrentParent']": {
        backgroundColor: `${theme.palette.grey[100]} !important`,
      },
      '&:focus > .MuiTreeItem-content .MuiTreeItem-label': {
        backgroundColor: 'transparent',
      },
      '&.Mui-selected > .MuiTreeItem-content > .MuiTreeItem-label': {
        backgroundColor: theme.palette.primary.light,
        '&:hover': {
          backgroundColor: '#FFC999',
          color: theme.palette.text.primary,
        },
      },
    },
    treeItemDefault: {
      backgroundColor: 'transparent',
      '&:hover': {
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.grey[100],
      },
    },
    treeItemCurrent: {
      fontWeight: 700,
      '&:hover': {
        backgroundColor: 'transparent',
        cursor: 'default',
      },
    },
    treeItemCurrentParent: {
      backgroundColor: theme.palette.grey[100],
      '&:hover': {
        backgroundColor: theme.palette.grey[100],
        cursor: 'default',
      },
    },
    treeItemIcon: {
      height: 16,
      marginRight: 10,
      paddingLeft: 8,
    },
    arrowIcon: {
      fill: theme.palette.primary.main,
    },
  }),
);
